import { ApolloCache } from '@provision/graphql-client';
import { MESSAGE_THREAD_FRAGMENT } from './conversation-message-reply.operations';
import {
  MessageReply,
  MessageThreadData,
  MessageThreadState,
} from './conversation-message-reply.types';

function updateMessageThread(
  cache: ApolloCache<unknown>,
  conversationMessageId: string,
  update: (data: MessageThreadData) => MessageThreadData,
): void {
  cache.updateFragment<MessageThreadData>(
    {
      id: cache.identify({ __typename: 'ConversationMessage', id: conversationMessageId }),
      fragment: MESSAGE_THREAD_FRAGMENT,
      fragmentName: 'MessageThread',
    },
    (data) => data && update(data),
  );
}

/**
 * Puts a reply on its cached parent message, replacing an entry with the same id and keeping its reactions.
 * A parent that isn't cached with its thread is left alone.
 */
export function upsertReplyInCache(cache: ApolloCache<unknown>, reply: MessageReply): void {
  if (!reply.parentConversationMessageId) {
    return;
  }
  updateMessageThread(cache, reply.parentConversationMessageId, (data) => {
    const replies = data.replies ?? [];
    const reactions = replies.find((existing) => existing.id === reply.id)?.reactions ?? [];
    return {
      ...data,
      replies: [
        ...replies.filter((existing) => existing.id !== reply.id),
        { ...reply, reactions, __typename: 'ConversationMessage' },
      ],
    };
  });
}

/** Replaces the thread state of a cached message; null clears it. */
export function writeThreadInCache(
  cache: ApolloCache<unknown>,
  conversationMessageId: string,
  thread: MessageThreadState | null,
): void {
  updateMessageThread(cache, conversationMessageId, (data) => ({
    ...data,
    thread: thread && { ...thread, __typename: 'ConversationMessageThread' },
  }));
}
